import { useCallback, useEffect, useState } from "react";
import { ApiError, api, apiUrl } from "../api";
import type { ProfileVideo } from "../types";
import { PageDialog } from "./ReferenceUI";

const reasons: Record<string, string> = {
  not_a_person: "В кадре должен быть владелец анкеты.",
  face_not_visible: "Лицо должно быть хорошо видно.",
  multiple_people: "В кадре должен быть только владелец анкеты.",
  explicit_content: "Видео нарушает правила контента.",
  text_or_contact_info: "Уберите текст и контактные данные из кадра.",
  low_quality: "Загрузите более чёткое видео.",
  other: "Загрузите другой ролик, соответствующий правилам.",
};
export function VideoPlayer({ video }: { video: ProfileVideo }) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return (
    <div className="profile-video-player">
      {/* biome-ignore lint/a11y/useMediaCaption: Captions are not available for user-uploaded profile videos. */}
      <video
        key={`${video.id}-${attempt}`}
        controls
        playsInline
        preload="metadata"
        crossOrigin="use-credentials"
        src={apiUrl(`${video.url}?attempt=${attempt}`)}
        poster={video.posterUrl}
        onError={() => setFailed(true)}
        aria-label="Видео анкеты"
      />
      {failed && (
        <div className="reference-error" role="alert">
          <p>Не удалось воспроизвести видео. Проверьте соединение и доступ к ролику.</p>
          <button
            type="button"
            className="reference-row"
            onClick={() => {
              setFailed(false);
              setAttempt((v) => v + 1);
            }}
          >
            Повторить воспроизведение
          </button>
        </div>
      )}
    </div>
  );
}
export function ProfileVideoManager({ onClose }: { onClose(): void }) {
  const [busy, setBusy] = useState(false);
  return (
    <PageDialog title="Моё видео" onClose={onClose} busy={busy}>
      <ProfileVideoContent onBusyChange={setBusy} />
    </PageDialog>
  );
}
export function ProfileVideoContent({ onBusyChange }: { onBusyChange?(busy: boolean): void }) {
  const [video, setVideo] = useState<ProfileVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, updateBusy] = useState(false);
  const setBusy = (next: boolean) => {
    updateBusy(next);
    onBusyChange?.(next);
  };
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setVideo((await api<{ video: ProfileVideo | null }>("/v1/me/video")).video);
    } catch {
      setError("Не удалось загрузить видео.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const upload = async (file?: File) => {
    if (!file || busy) return;
    if (file.size > 10 * 1024 * 1024 || file.size === 0) {
      setError("Выберите видео размером до 10 МБ.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.set("video", file);
      const result = await api<{ video: ProfileVideo }>("/v1/me/video", { method: "POST", body });
      setVideo(result.video);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "";
      setError(
        code === "video_exists"
          ? "В анкете уже есть ролик. Обновите экран, прежде чем загрузить другой."
          : ["video_too_large", "FST_REQ_FILE_TOO_LARGE"].includes(code)
            ? "Выберите видео размером до 10 МБ."
            : code === "invalid_video_duration"
              ? "Длительность видео — от 1 до 30 секунд."
              : ["video_processing_busy", "video_processing_unavailable"].includes(code)
                ? "Обработка временно недоступна. Попробуйте позже."
                : "Не удалось загрузить видео. Подойдут MP4, MOV или WebM, от 1 до 30 секунд, до 10 МБ.",
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!video || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/me/video/${video.id}`, { method: "DELETE" });
      setVideo(null);
      setConfirm(false);
    } catch {
      setError("Не удалось удалить видео. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="profile-media-panel">
      <p className="reference-note">
        Коротко расскажите о себе. Один ролик, от 1 до 30 секунд, до 10 МБ. MP4, MOV или WebM.
      </p>
      {loading ? (
        <p role="status">Загружаем видео…</p>
      ) : video ? (
        <>
          <VideoPlayer video={video} />
          <div className="reference-group">
            <p className="video-status" role="status">
              {video.moderationStatus === "approved"
                ? "Видео одобрено"
                : video.moderationStatus === "pending"
                  ? "Видео на проверке"
                  : "Видео отклонено"}
            </p>
          </div>
          <p className="reference-note">
            {video.moderationStatus === "pending"
              ? "До одобрения ролик виден только вам и модераторам."
              : video.moderationStatus === "rejected"
                ? (reasons[video.rejectionReason ?? "other"] ?? reasons.other)
                : "Ролик доступен в вашей анкете. Смотреть чужие видео можно с Premium."}
          </p>
          {confirm ? (
            <div className="reference-group">
              <p className="video-status">Удалить ролик? Для нового видео снова потребуется проверка.</p>
              <button type="button" className="reference-row" disabled={busy} onClick={() => void remove()}>
                Удалить видео
              </button>
              <button type="button" className="reference-row" disabled={busy} onClick={() => setConfirm(false)}>
                Оставить видео
              </button>
            </div>
          ) : (
            <button type="button" className="reference-row" disabled={busy} onClick={() => setConfirm(true)}>
              Заменить или удалить видео
            </button>
          )}
        </>
      ) : (
        !error && (
          <label className={`reference-upload video-upload ${busy ? "busy" : ""}`}>
            {busy ? "Загружаем и обрабатываем…" : "Загрузить видео"}
            <input
              type="file"
              aria-label="Загрузить видео"
              accept="video/mp4,video/quicktime,video/webm,.mov"
              disabled={busy}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                void upload(file);
              }}
            />
          </label>
        )
      )}
      {busy && (
        <p className="reference-note" role="status">
          Подождите, пока обработка завершится. Это может занять до полутора минут.
        </p>
      )}
      {error && (
        <div className="reference-error" role="alert">
          <p>{error}</p>
          <button type="button" className="reference-row" disabled={busy} onClick={() => void load()}>
            Обновить экран
          </button>
        </div>
      )}
    </section>
  );
}
export function ProfileVideoViewer({ id, onClose, onPremium }: { id: string; onClose(): void; onPremium(): void }) {
  const [video, setVideo] = useState<ProfileVideo | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setError(false);
    void api<{ video: ProfileVideo }>(`/v1/videos/${id}?attempt=${attempt}`)
      .then((r) => {
        if (!cancelled) setVideo(r.video);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === "premium_required") {
          onClose();
          onPremium();
        } else setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, attempt, onClose, onPremium]);
  return (
    <PageDialog title="Видео анкеты" onClose={onClose}>
      {error ? (
        <>
          <p className="reference-error" role="alert">
            Видео недоступно. Возможно, автор скрыл или удалил его.
          </p>
          <button type="button" className="reference-row" onClick={() => setAttempt((v) => v + 1)}>
            Повторить
          </button>
        </>
      ) : video ? (
        <VideoPlayer video={video} />
      ) : (
        <p role="status">Загружаем видео…</p>
      )}
    </PageDialog>
  );
}
